import { Injectable } from '@angular/core'
import {
  BasePageMetaResolver,
  BreadcrumbMeta,
  CmsService,
  ContentPageMetaResolver,
  OccEndpointsService,
  PageType,
  RoutingService,
  TranslationService,
  WindowRef
} from '@spartacus/core'
import { combineLatest, Observable, of } from 'rxjs'
import { distinctUntilChanged, switchMap } from 'rxjs/operators'
import { EBOGPMetaData, EbPage } from '../interfaces/eb-page.interface'

@Injectable({
  providedIn: 'root'
})
export class EbContentPageMetaResolver extends ContentPageMetaResolver {
  constructor(
    protected basePageMetaResolver: BasePageMetaResolver,
    protected cms: CmsService,
    protected routingService: RoutingService,
    protected translationService: TranslationService,
    protected occEndpoints: OccEndpointsService,
    protected winRef: WindowRef
  ) {
    super(basePageMetaResolver)
    this.pageType = PageType.CONTENT_PAGE
  }

  protected page$ = this.cms.getCurrentPage() as Observable<EbPage>

  resolveTitle(): Observable<string | undefined> {
    return this.page$.pipe(
      switchMap(page => {
        const metaTitle = page?.metaTitle ?? ''
        const title = [page?.title]

        if (metaTitle) {
          title.push(metaTitle)
        }

        return of(title.join(' | '))
      })
    )
  }

  resolveOGPMetaData(): Observable<EBOGPMetaData> {
    const baseUrlWithoutPrefixAndBaseSite = this.occEndpoints.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false
    })

    return this.page$.pipe(
      switchMap(page => {
        const imageUrl = page.logo?.includes(baseUrlWithoutPrefixAndBaseSite)
          ? page.logo
          : baseUrlWithoutPrefixAndBaseSite + page.logo
        return of({
          title: page.title || page.metaTitle,
          description: page.metaDescription,
          type: 'website',
          url: this.winRef.location.href,
          image: imageUrl || ''
        })
      })
    )
  }

  resolveBreadcrumbs(): Observable<BreadcrumbMeta[] | undefined> {
    return combineLatest([
      this.page$,
      this.routingService.getRouterState(),
      this.translationService.translate('orderHistory.orderHistory')
    ]).pipe(
      distinctUntilChanged(),
      switchMap(([page, routingData, orderHistoryTitle]) => {
        const crumbs = [] as BreadcrumbMeta[]
        if (page?.pageId === 'orderDetail') {
          const orderId =
            routingData.state.params.orderCode || routingData.state.url.split('/').pop()
          const items = [
            {
              label: orderHistoryTitle,
              link: '/my-account/orders'
            },
            {
              label: orderId,
              link: ''
            }
          ]
          crumbs.push(...items)
          return of(crumbs)
        }
        // Only add breadcrumb for content pages, not for category or product pages
        // They have their own dedicated resolvers
        if (page?.type !== 'CategoryPage' && page?.type !== 'ProductPage') {
          crumbs.push({
            label: page?.title,
            link: page?.label
          })
        }
        return of(crumbs)
      })
    )
  }
}
